import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { Modal, Button, Form } from "react-bootstrap";
import Select from "react-select";
import Breadcrums from "../../common/breadcrumbs";
import Loader from "../../common/loader";
import CustomPagination from "../../common/custompagination";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import * as productRatingServices from "../../services/productRating.services";
import * as productServices from "../../services/product.services";
import * as userServices from "../../services/user.service";
import { useTranslation } from "react-i18next";

const ProductRatingList = () => {
  const params = useParams();
  const { t } = useTranslation();
  const location = useLocation();

  const [list, setList] = useState([]);
  const [statsupdate, setStatusUpdate] = useState(false);
  const [datalength, setDataLength] = useState(0);
  const [itemperpage] = useState(10);
  const [page, setPage] = useState(1);
  const [loader, setLoader] = useState(true);
  const [globalsearch, setGlobalSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [starFilter, setStarFilter] = useState("all");

  // Modal State (Add / Edit)
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [productsList, setProductsList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [selectedProductOption, setSelectedProductOption] = useState(null);
  const [selectedUserOption, setSelectedUserOption] = useState(null);
  const [productLoader, setProductLoader] = useState(false);

  // Form State
  const [reviewerName, setReviewerName] = useState("");
  const [reviewerLocation, setReviewerLocation] = useState("");
  const [variantDetails, setVariantDetails] = useState("");
  const [varCarat, setVarCarat] = useState("");
  const [varOrigin, setVarOrigin] = useState("");
  const [varType, setVarType] = useState("");
  const [isVerifiedBuyer, setIsVerifiedBuyer] = useState(true);
  const [ratingVal, setRatingVal] = useState("5");
  const [commentText, setCommentText] = useState("");
  const [formStatus, setFormStatus] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const breadcrumbs = [
    { title: t("link_dashboard") || "Dashboard", url: "/admin/dashboard" },
    { title: "Product Ratings", url: "" },
  ];

  // Fetch product ratings list
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoader(true);
      const pg = params.pgno ? parseInt(params.pgno) : page;
      const payload = {
        page: pg,
        limit: itemperpage,
        search: globalsearch,
      };

      if (statusFilter !== "all") {
        payload.status = statusFilter === "active" ? true : false;
      }
      if (starFilter !== "all") {
        payload.rating = parseInt(starFilter, 10);
      }

      productRatingServices
        .List(payload)
        .then((res) => {
          if (res && (res.status || res.success || res.data?.status)) {
            const rowsList = res.list || res.data?.list || (Array.isArray(res.data) ? res.data : []);
            const totalRecs =
              res.total_records ||
              res.data?.total_records ||
              res.pagination?.total_items ||
              (Array.isArray(rowsList) ? rowsList.length : 0);
            setList(Array.isArray(rowsList) ? rowsList : []);
            setDataLength(totalRecs);
          } else {
            setList([]);
            setDataLength(0);
          }
          setLoader(false);
        })
        .catch((err) => {
          console.error("Fetch product ratings error: ", err);
          setLoader(false);
        });
    }, 300);

    return () => clearTimeout(timer);
  }, [location, page, params.pgno, globalsearch, statusFilter, starFilter, statsupdate]);

  // Fetch Products & Users for Modal dropdowns
  useEffect(() => {
    if (!showModal) return;
    setProductLoader(true);

    productServices
      .List({ page: 1, limit: 100 })
      .then((res) => {
        let prods = [];
        if (res.data && res.data.data && Array.isArray(res.data.data)) {
          prods = res.data.data;
        } else if (res.data && Array.isArray(res.data)) {
          prods = res.data;
        }
        const options = prods.map((p) => ({
          value: p.id,
          label: `${p.name} (₹${p.price})`,
        }));
        setProductsList(options);
      })
      .catch((err) => console.error("Fetch products error:", err));

    userServices
      .userList({ page: 1, limit: 100 })
      .then((res) => {
        let users = [];
        if (res.data && res.data.data && Array.isArray(res.data.data)) {
          users = res.data.data;
        } else if (res.data && res.data.list && Array.isArray(res.data.list)) {
          users = res.data.list;
        }
        const userOptions = users.map((u) => ({
          value: u.id,
          label: `${u.name || "User"} (${u.phone_number || u.email || u.id})`,
        }));
        setUsersList(userOptions);
        setProductLoader(false);
      })
      .catch((err) => {
        console.error("Fetch users error:", err);
        setProductLoader(false);
      });
  }, [showModal]);

  // Helper to sync variantDetails string when sub fields change
  const updateVariantDetailsString = (carat, origin, type) => {
    const parts = [];
    if (carat && carat.trim()) parts.push(carat.trim());
    if (origin && origin.trim()) parts.push(origin.trim());
    if (type && type.trim()) parts.push(type.trim());
    setVariantDetails(parts.join(" · "));
  };

  const handleOpenAddModal = () => {
    setEditId(null);
    setSelectedProductOption(null);
    setSelectedUserOption(null);
    setReviewerName("");
    setReviewerLocation("");
    setVariantDetails("");
    setVarCarat("");
    setVarOrigin("");
    setVarType("");
    setIsVerifiedBuyer(true);
    setRatingVal("5");
    setCommentText("");
    setFormStatus(true);
    setShowModal(true);
  };

  const handleOpenEditModal = (item) => {
    setEditId(item.id);
    setSelectedProductOption({
      value: item.product_id,
      label: item.product ? item.product.name : `Product #${item.product_id}`,
    });
    setSelectedUserOption(
      item.user_id
        ? {
            value: item.user_id,
            label: item.user?.name || `User #${item.user_id}`,
          }
        : null
    );
    setReviewerName(item.reviewer_name || item.user?.name || "");
    setReviewerLocation(item.reviewer_location || item.user?.city || "");
    setVariantDetails(item.variant_details || "");
    setVarCarat("");
    setVarOrigin("");
    setVarType("");
    setIsVerifiedBuyer(Boolean(item.is_verified_buyer));
    setRatingVal(String(item.rating || "5"));
    setCommentText(item.comment || "");
    setFormStatus(Boolean(item.status));
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditId(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedProductOption) {
      Swal.fire({
        ...SWAL_SETTINGS,
        icon: "warning",
        title: "Please select a product",
      });
      return;
    }

    setSubmitting(true);
    const payload = {
      product_id: selectedProductOption.value,
      user_id: selectedUserOption ? selectedUserOption.value : null,
      reviewer_name: reviewerName || null,
      reviewer_location: reviewerLocation || null,
      variant_details: variantDetails || null,
      is_verified_buyer: isVerifiedBuyer,
      rating: parseInt(ratingVal, 10),
      comment: commentText,
      status: formStatus,
    };

    const apiCall = editId
      ? productRatingServices.Edit({ id: editId, ...payload })
      : productRatingServices.Add(payload);

    apiCall
      .then((res) => {
        setSubmitting(false);
        const isSuccess = res.status || res.success || res.data?.status;
        if (isSuccess) {
          Swal.fire({
            ...SWAL_SETTINGS,
            icon: "success",
            title: res.message || res.data?.message || (editId ? "Rating updated!" : "Rating added!"),
          });
          setShowModal(false);
          setStatusUpdate(!statsupdate);
        } else {
          Swal.fire({
            ...SWAL_SETTINGS,
            icon: "error",
            title: res.message || res.data?.message || "Failed to save rating",
          });
        }
      })
      .catch((err) => {
        setSubmitting(false);
        Swal.fire({
          ...SWAL_SETTINGS,
          icon: "error",
          title: err.response?.data?.message || "Server Error",
        });
      });
  };

  // Toggle Status (Active / Inactive)
  const handleToggleStatus = (item) => {
    const newStatus = !item.status;
    productRatingServices
      .ChangeStatus({ id: item.id, status: newStatus })
      .then((res) => {
        if (res && (res.status || res.success || res.data?.status)) {
          Swal.fire({
            ...SWAL_SETTINGS,
            icon: "success",
            title: res.message || res.data?.message || `Status updated to ${newStatus ? "Active" : "Inactive"}`,
          });
          setStatusUpdate((prev) => !prev);
        }
      })
      .catch((err) => {
        Swal.fire({
          ...SWAL_SETTINGS,
          icon: "error",
          title: err.response?.data?.message || "Failed to update status",
        });
      });
  };

  // Delete Rating
  const handleDelete = (id) => {
    Swal.fire({
      ...SWAL_SETTINGS,
      title: "Are you sure you want to delete this rating?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      confirmButtonColor: "#d33",
    }).then((result) => {
      if (result.isConfirmed) {
        productRatingServices
          .Delete({ id })
          .then((res) => {
            if (res && (res.status || res.success || res.data?.status)) {
              Swal.fire({
                ...SWAL_SETTINGS,
                icon: "success",
                title: res.message || res.data?.message || "Rating deleted successfully!",
              });
              setStatusUpdate((prev) => !prev);
            } else {
              Swal.fire({
                ...SWAL_SETTINGS,
                icon: "error",
                title: res ? res.message : "Failed to delete rating",
              });
            }
          })
          .catch((err) => {
            Swal.fire({
              ...SWAL_SETTINGS,
              icon: "error",
              title: err.response?.data?.message || "Server Error",
            });
          });
      }
    });
  };

  // Render Stars Component
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <i
          key={i}
          className={`fas fa-star ${i <= rating ? "text-warning" : "text-muted"}`}
          style={{ fontSize: "14px", marginRight: "2px" }}
        ></i>
      );
    }
    return stars;
  };

  return (
    <div className="main-content">
      <Breadcrums breadcrumbs={breadcrumbs} />

      <div className="row row-sm">
        <div className="col-lg-12">
          <div className="card custom-card">
            <div className="card-body">
              {/* Header Controls & Filters */}
              <div className="row mb-3 align-items-center">
                <div className="col-md-3 mb-2 mb-md-0">
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search review / name..."
                      value={globalsearch}
                      onChange={(e) => {
                        setGlobalSearch(e.target.value);
                        setPage(1);
                      }}
                    />
                    {globalsearch && (
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setGlobalSearch("")}
                      >
                        <i className="fa fa-times"></i>
                      </button>
                    )}
                  </div>
                </div>

                <div className="col-md-2 mb-2 mb-md-0">
                  <select
                    className="form-select"
                    value={starFilter}
                    onChange={(e) => {
                      setStarFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="all">All Stars ⭐</option>
                    <option value="5">5 Stars ⭐⭐⭐⭐⭐</option>
                    <option value="4">4 Stars ⭐⭐⭐⭐</option>
                    <option value="3">3 Stars ⭐⭐⭐</option>
                    <option value="2">2 Stars ⭐⭐</option>
                    <option value="1">1 Star ⭐</option>
                  </select>
                </div>

                <div className="col-md-2 mb-2 mb-md-0">
                  <select
                    className="form-select"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Hidden</option>
                  </select>
                </div>

                <div className="col-md-5 text-end">
                  <button className="btn btn-primary" onClick={handleOpenAddModal}>
                    <i className="fa fa-plus me-1"></i> Add Product Rating
                  </button>
                </div>
              </div>

              {/* Table Data */}
              {loader ? (
                <Loader />
              ) : (
                <div className="table-responsive">
                  <table className="table table-bordered text-nowrap border-bottom align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: "50px" }}>#</th>
                        <th>Product</th>
                        <th>Customer / Reviewer</th>
                        <th>Rating</th>
                        <th>Review &amp; Details (Origin/Carat/Type)</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th style={{ width: "130px" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.length > 0 ? (
                        list.map((item, index) => (
                          <tr key={item.id}>
                            <td>{(page - 1) * itemperpage + index + 1}</td>
                            <td>
                              <strong>{item.product ? item.product.name : `Product #${item.product_id}`}</strong>
                            </td>
                            <td>
                              <strong className="text-primary">
                                {item.reviewer_name || item.user?.name || `User #${item.user_id}`}
                              </strong>
                              {item.reviewer_location ? (
                                <div className="text-muted small">
                                  <i className="fa fa-map-marker-alt text-danger me-1"></i>
                                  {item.reviewer_location}
                                </div>
                              ) : item.user?.city ? (
                                <div className="text-muted small">
                                  <i className="fa fa-map-marker-alt me-1"></i>
                                  {item.user.city}
                                </div>
                              ) : null}
                            </td>
                            <td>
                              <div>{renderStars(item.rating)}</div>
                              <small className="fw-bold text-dark">{item.rating}/5</small>
                            </td>
                            <td style={{ maxWidth: "340px", whiteSpace: "normal" }}>
                              <div>{item.comment || <em className="text-muted">No comment</em>}</div>
                              <div className="mt-1 d-flex flex-wrap gap-1 align-items-center">
                                {item.is_verified_buyer && (
                                  <span className="badge bg-success-light text-success fw-normal me-1">
                                    ✓ Verified Buyer
                                  </span>
                                )}
                                {item.variant_details ? (
                                  <span className="badge bg-light text-dark border fw-normal">
                                    <i className="fa fa-gem me-1 text-primary"></i>
                                    {item.variant_details}
                                  </span>
                                ) : (
                                  <span className="text-muted small italic">No variant specs</span>
                                )}
                              </div>
                            </td>
                            <td>
                              {item.status ? (
                                <button
                                  className="btn btn-sm btn-success px-3"
                                  onClick={() => handleToggleStatus(item)}
                                  title="Active (Visible on App) - Click to set Inactive"
                                >
                                  Active
                                </button>
                              ) : (
                                <button
                                  className="btn btn-sm btn-secondary px-3"
                                  onClick={() => handleToggleStatus(item)}
                                  title="Inactive (Hidden from App) - Click to set Active"
                                >
                                  Inactive
                                </button>
                              )}
                            </td>
                            <td>
                              {item.formatted_date ||
                                (item.created_at || item.createdAt
                                  ? new Date(item.created_at || item.createdAt).toLocaleDateString("en-GB", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })
                                  : "-")}
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary me-2"
                                onClick={() => handleOpenEditModal(item)}
                                title="Edit Rating & Details"
                              >
                                <i className="fa fa-edit"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDelete(item.id)}
                                title="Delete Rating"
                              >
                                <i className="fa fa-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center py-4 text-muted">
                            No product ratings found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {datalength > itemperpage && (
                <CustomPagination
                  dataLength={datalength}
                  itemPerPage={itemperpage}
                  routePath="/admin/product-rating/list"
                  currentPage={params.pgno ? parseInt(params.pgno) : 1}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add / Edit Rating Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{editId ? "Edit Product Rating & Review" : "Add Product Rating & Review"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Form.Label className="fw-bold">Select Product *</Form.Label>
                <Select
                  options={productsList}
                  value={selectedProductOption}
                  onChange={setSelectedProductOption}
                  placeholder="Search product..."
                  isLoading={productLoader}
                  isSearchable
                />
              </div>

              <div className="col-md-6 mb-3">
                <Form.Label className="fw-bold">Link Registered User (Optional)</Form.Label>
                <Select
                  options={usersList}
                  value={selectedUserOption}
                  onChange={setSelectedUserOption}
                  placeholder="Select registered user..."
                  isLoading={productLoader}
                  isClearable
                  isSearchable
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <Form.Label className="fw-bold">Reviewer / Customer Name *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Rohit Sharma"
                  value={reviewerName}
                  onChange={(e) => setReviewerName(e.target.value)}
                  required
                />
              </div>

              <div className="col-md-6 mb-3">
                <Form.Label className="fw-bold">City / Location</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Jaipur"
                  value={reviewerLocation}
                  onChange={(e) => setReviewerLocation(e.target.value)}
                />
              </div>
            </div>

            <div className="border rounded p-3 mb-3 bg-light">
              <Form.Label className="fw-bold text-primary mb-2">
                <i className="fa fa-gem me-1"></i> Product Variant / Specs Details (Carat, Origin, Type)
              </Form.Label>
              <Form.Group className="mb-2">
                <Form.Control
                  type="text"
                  placeholder="e.g. 4.25 ct Ceylon · Ring in Silver"
                  value={variantDetails}
                  onChange={(e) => setVariantDetails(e.target.value)}
                />
                <Form.Text className="text-muted">
                  This detail badge appears on the review card (e.g., <strong>4.25 ct Ceylon · Ring in Silver</strong>).
                </Form.Text>
              </Form.Group>

              <div className="row g-2 mt-1">
                <div className="col-md-4">
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="Weight/Carat (e.g. 4.25 ct)"
                    value={varCarat}
                    onChange={(e) => {
                      setVarCarat(e.target.value);
                      updateVariantDetailsString(e.target.value, varOrigin, varType);
                    }}
                  />
                </div>
                <div className="col-md-4">
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="Origin (e.g. Ceylon)"
                    value={varOrigin}
                    onChange={(e) => {
                      setVarOrigin(e.target.value);
                      updateVariantDetailsString(varCarat, e.target.value, varType);
                    }}
                  />
                </div>
                <div className="col-md-4">
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="Type (e.g. Ring in Silver)"
                    value={varType}
                    onChange={(e) => {
                      setVarType(e.target.value);
                      updateVariantDetailsString(varCarat, varOrigin, e.target.value);
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <Form.Label className="fw-bold">Rating Stars *</Form.Label>
                <Form.Select value={ratingVal} onChange={(e) => setRatingVal(e.target.value)}>
                  <option value="5">⭐⭐⭐⭐⭐ (5 Stars)</option>
                  <option value="4">⭐⭐⭐⭐ (4 Stars)</option>
                  <option value="3">⭐⭐⭐ (3 Stars)</option>
                  <option value="2">⭐⭐ (2 Stars)</option>
                  <option value="1">⭐ (1 Star)</option>
                </Form.Select>
              </div>

              <div className="col-md-6 mb-3 d-flex align-items-center">
                <Form.Check
                  type="checkbox"
                  id="is_verified_buyer_chk"
                  label="✓ Show 'Verified Buyer' Badge"
                  checked={isVerifiedBuyer}
                  onChange={(e) => setIsVerifiedBuyer(e.target.checked)}
                  className="fw-bold text-success mt-4"
                />
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Comment / Review Text</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Write customer review text..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="form_status_sw"
                label="Status Active (Visible on Mobile App)"
                checked={formStatus}
                onChange={(e) => setFormStatus(e.target.checked)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {editId ? "Update Rating" : "Save Rating"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductRatingList;
